import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import PageShell from '../components/PageShell.jsx';
import LeadForm from '../components/LeadForm.jsx';
import ProgramCard from '../components/ProgramCard.jsx';
import Seo from '../components/Seo.jsx';
import JsonLd, { buildFaqPageSchema } from '../components/JsonLd.jsx';
import FaqAccordion from '../components/FaqAccordion.jsx';
import {
  fillTemplate,
  getUniversityCopy,
  resolveUniversitySeo
} from '../content/index.js';
import { locationLabel } from '../utils/format.js';
import UniversityLogo from '../components/UniversityLogo.jsx';
import CampusImage, { CampusCreditLinks, getCampusImage } from '../components/CampusImage.jsx';
import { catalogLevels } from '../utils/courseLevels.js';

export default function UniversityPage() {
  const { slug } = useParams();
  const copy = getUniversityCopy();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [selectedLevel, setSelectedLevel] = useState(searchParams.get('level') || '');
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    setSearchInput(searchParams.get('search') || '');
    setSelectedLevel(searchParams.get('level') || '');
  }, [searchParams]);

  useEffect(() => {
    async function load() {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const query = new URLSearchParams();
      const search = searchParams.get('search');
      const level = searchParams.get('level');
      if (search) query.set('search', search);
      if (level) query.set('level', level);

      try {
        const res = await fetch(
          `/api/universities/${slug}${query.toString() ? `?${query}` : ''}`
        );
        if (res.status === 404) {
          setState({ loading: false, error: 'University not found.', data: null });
          return;
        }
        if (!res.ok) throw new Error('Failed');
        const payload = await res.json();
        setState({ loading: false, error: null, data: payload.data });
      } catch {
        setState({ loading: false, error: 'Unable to load university.', data: null });
      }
    }

    load();
  }, [slug, searchParams]);

  const university = state.data?.university;
  const courses = state.data?.courses || [];
  const levels = catalogLevels(state.data?.levels);
  const courseStats = state.data?.courseStats;

  const seoPack = useMemo(
    () => resolveUniversitySeo(slug, university?.name),
    [slug, university?.name]
  );
  const faqSchema = useMemo(() => buildFaqPageSchema(seoPack.faqs), [seoPack.faqs]);

  const resultLabel = useMemo(() => {
    if (!courseStats) return '';
    if (courseStats.filtered === courseStats.total) {
      return `${courseStats.total} program${courseStats.total === 1 ? '' : 's'}`;
    }
    return `Showing ${courseStats.filtered} of ${courseStats.total} programs`;
  }, [courseStats]);

  function applyFilters(level = selectedLevel, search = searchInput) {
    const next = {};
    if (search.trim()) next.search = search.trim();
    if (level) next.level = level;
    setSearchParams(next);
  }

  function handleSearchSubmit(event) {
    event.preventDefault();
    applyFilters();
  }

  function handleLevelChange(code) {
    setSelectedLevel(code);
    applyFilters(code, searchInput);
  }

  return (
    <PageShell>
      {university && (
        <>
          <Seo title={seoPack.title} description={seoPack.description} />
          <JsonLd data={faqSchema} />
        </>
      )}

      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <span>{university?.name || 'University'}</span>
        </div>
      </div>

      {state.loading && !university && (
        <p className="status-text page-status container">Loading university...</p>
      )}
      {state.error && (
        <p className="status-text error page-status container">{state.error}</p>
      )}

      {university && (
        <>
          <div className="container university-layout">
            <div className="university-main">
              <section className="uni-hero">
                <div className={`uni-hero-inner${getCampusImage(university.slug) ? ' has-campus-image' : ''}`}>
                  {getCampusImage(university.slug) && (
                    <>
                      <CampusImage
                        slug={university.slug}
                        name={university.name}
                        variant="hero"
                        className="uni-hero-bg"
                      />
                      <span className="uni-hero-scrim" aria-hidden />
                    </>
                  )}
                  <div className="uni-hero-top">
                    <UniversityLogo
                      slug={university.slug}
                      name={university.shortName || university.name}
                      logoUrl={university.logoUrl}
                      className="university-logo university-logo-hero"
                    />
                    <span className="pill">{university.isOnlineOnly ? 'Online' : 'Online + Hybrid'}</span>
                  </div>
                  <div className="uni-hero-heading">
                    <div>
                      <h1>{seoPack.h1 || university.name}</h1>
                      <div className="university-banner-meta">
                        {locationLabel(university) && <span>{locationLabel(university)}</span>}
                        {courseStats && (
                          <>
                            {locationLabel(university) && <span className="meta-dot">•</span>}
                            <span>
                              {courseStats.total} program{courseStats.total === 1 ? '' : 's'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="university-banner-desc">
                    {seoPack.intro || university.description}
                  </p>
                  <CampusCreditLinks slug={university.slug} className="campus-credit campus-credit-hero" />
                </div>
              </section>

              <section className="university-programs">
                <header className="programs-header">
                  <div>
                    <h2>{copy.programsHeading}</h2>
                    <p>{resultLabel || copy.programsIntro}</p>
                  </div>
                </header>

              <form className="programs-toolbar" onSubmit={handleSearchSubmit}>
                <input
                  className="search-input programs-search"
                  type="search"
                  placeholder={copy.searchPlaceholder}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <button className="cta-button" type="submit">
                  Search
                </button>
              </form>

              {levels.length > 0 && (
                <div className="level-toggle programs-filters">
                  <button
                    type="button"
                    className={`level-pill ${selectedLevel === '' ? 'active' : ''}`}
                    onClick={() => handleLevelChange('')}
                  >
                    All levels
                  </button>
                  {levels.map((level) => (
                    <button
                      key={level.code}
                      type="button"
                      className={`level-pill ${selectedLevel === level.code ? 'active' : ''}`}
                      onClick={() => handleLevelChange(level.code)}
                    >
                      {level.name}
                    </button>
                  ))}
                </div>
              )}

              {state.loading && <p className="status-text">Updating results...</p>}

              <div className="course-grid">
                {!state.loading && courses.length === 0 && (
                  <article className="course-card course-card-empty">
                    <h3>{copy.emptyPrograms}</h3>
                    <p className="course-meta">Try a different keyword or level filter.</p>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        setSearchInput('');
                        setSelectedLevel('');
                        setSearchParams({});
                      }}
                    >
                      Clear filters
                    </button>
                  </article>
                )}
                {courses.map((course) => (
                  <ProgramCard
                    key={course.id}
                    to={`/universities/${slug}/courses/${course.slug}`}
                    slug={course.slug}
                    name={course.name}
                    universityName={university.name}
                    universitySlug={slug}
                    levelCode={course.levelCode}
                    mode={course.mode}
                    durationMonths={course.durationMonths}
                    totalFeeMin={course.totalFeeMin}
                    currency={course.currency}
                  />
                ))}
              </div>
            </section>
            </div>

            <aside className="university-sidebar">
              <div className="university-sidebar-sticky">
                <LeadForm
                  title={copy.leadTitle}
                  subtitle={fillTemplate(copy.leadSubtitle, { university: university.name })}
                  leadSource={`university_${slug}`}
                />
              </div>
            </aside>
          </div>

          {seoPack.faqs?.length > 0 && (
            <FaqAccordion heading={copy.faqHeading} faqs={seoPack.faqs} />
          )}
        </>
      )}
    </PageShell>
  );
}
